/**
 * TagHistorySection - Shows user's previously released tags and cooldown status
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, CheckCircle, XCircle, Loader2, History, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TagHistorySection = () => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/tags/history');
      setHistory(data);
    } catch (err) {
      console.error('Failed to fetch tag history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleReclaim = async (tag) => {
    try {
      const { data } = await api.post('/v1/tags/claim', { tag });
      toast.success(`Successfully reclaimed @${data.tag}!`);
      fetchHistory();
      // Refresh the page to update the main tag section
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reclaim tag');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return null; // Don't show section if no history
  }

  return (
    <Card data-testid="tag-history-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-muted-foreground" />
          Tag History
        </CardTitle>
        <CardDescription>
          Previously released tags and their cooldown status
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {history.map((item, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${item.is_available ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  {item.is_available ? (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-amber-500" />
                  )}
                </div>
                <div>
                  <p className="font-mono font-medium">{item.tag_display}</p>
                  <p className="text-xs text-muted-foreground">
                    Released {new Date(item.released_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {item.is_available ? (
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                    Available
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                    {item.days_remaining} days left
                  </Badge>
                )}
                
                {item.is_available && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleReclaim(item.tag)}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Reclaim
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TagHistorySection;
